import React, { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { observer } from 'mobx-react';
import { useLocale } from 'next-intl';
import axios from 'axios';
import Image from 'next/image';
import Link from 'next/link';
import Product from '../products/Product';
import SearchIcon from '../svg/SearchIcon';
import DeleteIcon from '../svg/DeleteIcon';
import MicIcon from '../svg/MicIcon'; // Add a microphone icon
import wooCommerceStore from '@/stores/woocommerceStore';
import studioStore from '@/stores/studioStore';

const Search = observer(() => {
  const t = useTranslations('General');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const locale = useLocale();
  const searchContainerRef = useRef(null);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (query.length > 0) {
        const fetchProducts = async () => {
          setLoading(true);
          try {
            const { data } = await axios.get(`/api/search`, { params: { query } });
            setResults(data);
            setNoResults(data.length === 0);
          } catch (error) {
            console.error('Error fetching products:', error);
            setNoResults(true);
          } finally {
            setLoading(false);
          }
        };

        fetchProducts();
      } else {
        setResults([]);
        setNoResults(false);
      }
    }, 1000);

    return () => clearTimeout(delaySearch);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setNoResults(false);
  };

  const startVoiceRecognition = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      alert('Your browser does not support voice recognition.');
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = locale === 'he' ? 'he-IL' : locale || 'en-US';
    recognition.interimResults = false;
    recognition.continuous = false;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);

    recognition.onresult = (event) => {
      let transcript = event.results[0][0].transcript.trim();
      transcript = transcript.replace(/[.,!?]$/, '');
      setQuery(transcript);
      setIsFocused(true);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const primaryColor = studioStore?.settings?.colors?.primary_color;

  return (
    <div className="search-container relative" ref={searchContainerRef}>
      <div className="flex items-center md:border text-primary-studio border-color-primary-studio rounded-2xl p-2 py-1.5 bg-white md:bg-transparent font-semibold w-full min-w-[15rem]">
        <input
          type="search"
          placeholder={isListening ? t('listening') : t('search')}
          className="grow bg-transparent focus:outline-none"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          name="search"
          title={isListening ? t('listening') : t('search')}
          autoComplete="off"
        />
        {query.length > 0 ? (
          <button type="button" className="text-gray-500" onClick={clearSearch}>
            <DeleteIcon stroke={primaryColor} />
          </button>
        ) : (
          <SearchIcon className="text-gray-500" stroke={primaryColor} />
        )}
        <button type="button" className="ms-1 text-gray-500" onClick={startVoiceRecognition} title={t('voice_search')}>
          <MicIcon stroke={isListening ? 'red' : primaryColor} />
        </button>
      </div>

      {loading && isFocused && (
        <div className="absolute left-0 right-0 mt-1 bg-white rounded shadow-lg z-10 p-2 text-center text-gray-500">
          {t('loading')}...
        </div>
      )}

      {!loading && results.length > 0 && isFocused && (
        <div className="absolute left-0 right-0 mt-1 bg-white rounded shadow-lg z-10 overflow-y-auto md:overflow-hidden w-full md:w-[650px] max-h-[530px]">
          <div className="flex flex-wrap">
            <div className="w-full md:w-1/3">
              <h3 className="text-sm font-semibold p-2 bg-primary-light-studio text-primary-studio">
                {t('search_results')}
              </h3>
              <ul className="md:max-h-[450px] md:overflow-y-auto">
                {results.slice(0, 15).map((product) => (
                  <li
                    key={product.id}
                    className="p-2 hover:bg-gray-100 cursor-pointer flex items-center"
                    onMouseDown={() => wooCommerceStore.showProductModal(product)}
                  >
                    <Image
                      src={product.images?.[0]?.src}
                      alt={product.name}
                      className="w-8 h-8 object-cover rounded me-2"
                      width={48}
                      height={48}
                    />
                    <span className="truncate">{product.name}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full md:w-2/3">
              <h3 className="text-sm font-semibold p-2 bg-primary-light-studio text-primary-studio">
                {t('featured_results')}
              </h3>
              <div className="md:max-h-[450px] md:overflow-y-auto">
                <div className="md:grid md:grid-cols-2 md:gap-2 products-background p-2">
                  {results.slice(0, 8).map((product) => (
                    <div key={product.id}>
                      <Product product={product} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {results.length > 15 && (
            <div className="p-2 bg-primary-light-studio hover:bg-primary-studio text-center">
              <Link href={`/search?q=${encodeURIComponent(query)}`} className="hover:underline text-primary-studio hover:text-white">
                {t('view_all_results')}
              </Link>
            </div>
          )}
        </div>
      )}

      {!loading && noResults && isFocused && (
        <div className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10 p-2 text-center text-gray-500">
          {t('no_results')}
        </div>
      )}
    </div>
  );
});

export default Search;
